import React, { useMemo } from "react";

import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { ContainerStatus } from "Models/marine.traffic";
import { ShipmentsTracking } from "Models/shipments/shipments.tracking";
import { ShipmentTrackingStatus, ShipmentType } from "Util/enums";
import {
  Wrapper,
  LogoWrapper,
  HeaderInfoWrapper,
  HeaderInfoTitle,
  LabelWrapper,
  Label,
  Value,
  LabelContainer,
} from "./styles";

interface HeaderProps {
  containerStatus?: ContainerStatus;
  shipment?: ShipmentsTracking;
  logo?: string;
  customerName?: string;
}

const canRenderEstimateDate: string[] = [
  ShipmentTrackingStatus.SHIPMENT_COMPLETED,
  ShipmentTrackingStatus.ARRIVED_AT_PORT_OR_DISCHARGE,
  ShipmentTrackingStatus.WAITING_AT_DESTINATION_PORT,
  ShipmentTrackingStatus.LEFT_THE_DESTINATION_PORT,
];

export const Header = (props: HeaderProps) => {
  const { containerStatus, shipment, logo, customerName } = props;
  const { t: translate } = useTranslation();

  const shipmentCustomerName: string = useMemo((): string => {
    if (customerName) {
      return customerName;
    }

    if (shipment?.user?.name) {
      const userName = shipment.user.name;

      return userName;
    }

    return "";
  }, [shipment]);

  const shipmentEstimateDate: string = useMemo((): string => {
    if (!containerStatus?.finalPortPredictiveArrivalUtcLast) return "";

    const date = new Date(containerStatus?.finalPortPredictiveArrivalUtcLast);
    const dayOfWeek = formatInTimeZone(date, "UTC", "eeee");
    const day = formatInTimeZone(date, "UTC", "d");
    const month = formatInTimeZone(date, "UTC", "LLLL");
    const year = formatInTimeZone(date, "UTC", "yyyy");

    return `${dayOfWeek}, ${day} ${month} ${year}`;
  }, [containerStatus]);

  const isAirTracking = useMemo(() => shipment?.shipmentType === ShipmentType.AIR, [shipment]);

  const renderShipmentStatus = () => {
    if (shipment?.company?.configs?.isShipmentPlannedEtaAccessDisabled) return null;

    if (
      shipment?.shipmentTracking?.shipmentStatus &&
      canRenderEstimateDate.includes(shipment?.shipmentTracking?.shipmentStatus?.toLowerCase()) &&
      !shipmentEstimateDate.trim().length
    )
      return null;

    return (
      <LabelWrapper>
        <Label>
          {isAirTracking
            ? translate("estimatedTimeOfAirportArrival")
            : translate("estimatedTimeOfPortArrival")}
          :
        </Label>
        <Value>{shipmentEstimateDate}</Value>
      </LabelWrapper>
    );
  };

  const hasShipmentCompleted = useMemo(() => {
    if (
      shipment?.shipmentTracking?.shipmentStatus &&
      shipment.shipmentTracking.shipmentStatus
        .toLowerCase()
        .includes(ShipmentTrackingStatus.SHIPMENT_COMPLETED)
    )
      return true;

    return false;
  }, [shipment]);

  const greetingMessage = useMemo(() => {
    const namePart = shipmentCustomerName ? `, ${shipmentCustomerName}.` : ".";
    return `${translate("hi")}${namePart} ${translate("thisIsYourShipmentInformation")}!`;
  }, [shipmentCustomerName, translate]);

  const jobReferences = useMemo(() => {
    return shipment?.jobs?.map((job) => `#${job.reference}`).join(", ");
  }, [shipment]);

  const hasJobs = useMemo(() => {
    return !!shipment?.jobs?.length;
  }, [shipment]);

  return (
    <Wrapper>
      <HeaderInfoWrapper>
        <LogoWrapper>
          {shipment?.company?.logo ? (
            <img src={shipment?.company?.logo?.mediaLink} width="225" />
          ) : (
            <img src={logo} width="225" />
          )}
        </LogoWrapper>

        <HeaderInfoTitle>{greetingMessage}</HeaderInfoTitle>

        <LabelContainer>
          {hasJobs && (
            <LabelWrapper>
              <Label>
                {translate("orders")}: <Value>{jobReferences}</Value>
              </Label>
            </LabelWrapper>
          )}

          <LabelWrapper>
            {hasShipmentCompleted ? (
              <Label>
                {translate("yourShipmentHasArrivedAt")}:{" "}
                <Value>{containerStatus?.finalPortName}</Value>
              </Label>
            ) : (
              <Label>
                {translate("yourShipmentIsExpectedToArriveAt")}:{" "}
                <Value>{containerStatus?.finalPortName}</Value>
              </Label>
            )}
          </LabelWrapper>

          {renderShipmentStatus()}
        </LabelContainer>
      </HeaderInfoWrapper>
    </Wrapper>
  );
};
